/* eslint-disable react/display-name, react/require-default-props */
import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/core';
import { ChevronDownIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import i18n from '../../../locales/i18n';
import { AppointmentAvailability } from '../../../firebase/firestore/documents/appointmentAvaiability';
import TimezoneSelect, { TimezoneValue } from './TimezoneSelect';

type TimeSectionProps = {
  startDate: Date | null | undefined;
  startTime: Date | null | undefined;
  endTime: Date | null | undefined;
  scheduleDetail?: AppointmentAvailability;
  isCustomTimezoneCalendar: boolean;
  selectedTimezone: TimezoneValue | undefined;
  getCurrentTimeInSelectedTZ: () => Date;
  onStartTimeChange: (date: Date) => void;
  onEndTimeChange: (date: Date) => void;
  onTimezoneChange: (value: TimezoneValue) => void;
};

export const TimeSelection = ({
  startDate,
  startTime,
  endTime,
  scheduleDetail,
  isCustomTimezoneCalendar,
  selectedTimezone,
  getCurrentTimeInSelectedTZ,
  onStartTimeChange,
  onEndTimeChange,
  onTimezoneChange,
}: TimeSectionProps): JSX.Element => {
  const { t } = useTranslation('calendar');

  const tzNow = getCurrentTimeInSelectedTZ();
  const isToday = moment(startDate).isSame(tzNow, 'day');

  const startMinTime = isToday ? tzNow : undefined;
  const startMaxTime = isToday ? moment(startDate).endOf('day').toDate() : undefined;

  // eslint-disable-next-line no-nested-ternary
  const endMinTime = isToday
    ? !startTime
      ? moment(tzNow).add(30, 'minutes').toDate()
      : moment(startTime).add(30, 'minutes').toDate()
    : undefined;
  const endMaxTime = isToday ? moment(startDate).endOf('day').toDate() : undefined;

  return (
    <>
      <Flex mt={5} align="center" className="time-picker-box">
        <Box>
          <Text mb={2}>{t('eventForm.startTime')}</Text>
          <Box className="time-input-group" zIndex={3}>
            <DatePicker
              locale={i18n.language}
              selected={startTime}
              minTime={startMinTime}
              maxTime={startMaxTime}
              disabled={!!startTime && !!scheduleDetail?.id}
              onChange={/* istanbul ignore next */ (date) => onStartTimeChange(date as Date)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={30}
              timeCaption={t('eventForm.timeCaption')}
              dateFormat="p"
            />
            <ChevronDownIcon />
          </Box>
        </Box>

        <Box ml={4}>
          <Text mb={2}>{t('eventForm.endTime')}</Text>
          <Box className="time-input-group">
            <DatePicker
              locale={i18n.language}
              selected={endTime}
              minTime={endMinTime}
              maxTime={endMaxTime}
              openToDate={startTime ? moment(startTime).toDate() : moment().toDate()}
              disabled={!!endTime && !!scheduleDetail?.id}
              onChange={/* istanbul ignore next */ (date) => onEndTimeChange(date as Date)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={30}
              timeCaption={t('eventForm.timeCaption')}
              dateFormat="p"
            />
            <ChevronDownIcon />
          </Box>
        </Box>
      </Flex>

      {isCustomTimezoneCalendar && (
        <Box mt={5}>
          <TimezoneSelect
            value={selectedTimezone || { name: '', offset: '' }}
            onChange={onTimezoneChange}
            label={t('availabilityForm.timezoneLabel')}
            id="calendarBookingTimezoneSelect"
          />
        </Box>
      )}
    </>
  );
};
